import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'contexts/AuthContext'
import {
    getMyPanel,
    removeItemFromPanel,
    reorderPanelItems,
    getPersonalNotes,
    createPersonalNote,
    updatePersonalNote,
    deletePersonalNote
} from 'service/PersonalPanelService'
import AudioPlayer from 'components/AudioPlayer/AudioPlayer'
import styles from './PersonalPanelPage.module.scss'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { CopyIcon } from 'components/CustomIcons'

const PersonalPanelPage = () => {
    const navigate = useNavigate()
    const { isAuthenticated, user } = useAuth()
    const [panel, setPanel] = useState(null)
    const [loading, setLoading] = useState(true)
    const [playingMusicId, setPlayingMusicId] = useState(null)
    const [copiedId, setCopiedId] = useState(null)
    const [draggedItemId, setDraggedItemId] = useState(null)
    const [dragOverIndex, setDragOverIndex] = useState(null)
    const [notesTab, setNotesTab] = useState('notes')
    const [notes, setNotes] = useState([])
    const [newNoteText, setNewNoteText] = useState('')
    const [notesLoading, setNotesLoading] = useState(false)
    const newNoteInputRef = useRef(null)
    const copiedTimeoutRef = useRef(null)

    const handleCopyWithNotification = (text, itemId) => {
        navigator.clipboard.writeText(text)
        setCopiedId(itemId)
        
        if (copiedTimeoutRef.current) {
            clearTimeout(copiedTimeoutRef.current)
        }
        
        copiedTimeoutRef.current = setTimeout(() => {
            setCopiedId(null)
        }, 2000)
    }

    const resizeTextareaElement = (element) => {
        if (!element) return
        element.style.height = 'auto'
        element.style.height = `${element.scrollHeight}px`
    }

    const autoResizeTextarea = (event) => {
        resizeTextareaElement(event.target)
    }

    const formatNoteDate = (dateString) => {
        const date = new Date(dateString)
        if (Number.isNaN(date.getTime())) return ''
        return new Intl.DateTimeFormat('ru-RU', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date)
    }

    const visibleNotes = notes.filter((note) => {
        if (notesTab === 'archive') return note.archived
        return !note.archived
    })

    useEffect(() => {
        if (!isAuthenticated) {
            // Показываем инструкцию для неавторизованных
            setLoading(false)
            return
        }
        loadPanel()
    }, [isAuthenticated])

    useEffect(() => {
        if (!isAuthenticated) {
            setNotes([])
            return
        }
        loadNotes()
    }, [isAuthenticated, user?.id])

    useEffect(() => {
        return () => {
            if (copiedTimeoutRef.current) {
                clearTimeout(copiedTimeoutRef.current)
            }
        }
    }, [])

    const loadNotes = async () => {
        try {
            setNotesLoading(true)
            const response = await getPersonalNotes()
            const notesData = response.data?.results || response.data || []
            setNotes(Array.isArray(notesData) ? notesData : [])
        } catch (error) {
            console.error('Error loading notes:', error)
            setNotes([])
        } finally {
            setNotesLoading(false)
        }
    }

    const handleCreateNoteOnBlur = async () => {
        const trimmed = newNoteText.trim()
        if (!trimmed) return

        try {
            const response = await createPersonalNote(trimmed)
            const createdNote = response.data
            setNotes((prev) => [createdNote, ...prev])
            setNewNoteText('')
            resizeTextareaElement(newNoteInputRef.current)
        } catch (error) {
            console.error('Error creating note:', error)
        }
    }

    const handleUpdateNoteText = async (noteId, text) => {
        // Оптимистичное обновление
        setNotes((prev) => prev.map((note) => {
            if (note.id !== noteId) return note
            return {
                ...note,
                text
            }
        }))

        try {
            await updatePersonalNote(noteId, { text })
        } catch (error) {
            console.error('Error updating note:', error)
        }
    }

    const handleArchiveToggle = async (noteId, archived) => {
        // Оптимистичное обновление
        setNotes((prev) => prev.map((note) => {
            if (note.id !== noteId) return note
            return {
                ...note,
                archived
            }
        }))

        try {
            await updatePersonalNote(noteId, { archived })
        } catch (error) {
            console.error('Error archiving note:', error)
        }
    }

    const handleDeleteNote = async (noteId) => {
        // Оптимистичное удаление
        setNotes((prev) => prev.filter((note) => note.id !== noteId))

        try {
            await deletePersonalNote(noteId)
        } catch (error) {
            console.error('Error deleting note:', error)
            // Перезагружаем заметки в случае ошибки
            loadNotes()
        }
    }

    const handleNoteBlur = async (noteId, text) => {
        const trimmed = text.trim()
        // Если заметка пустая, удаляем её
        if (!trimmed) {
            await handleDeleteNote(noteId)
        }
    }

    const loadPanel = async () => {
        try {
            setLoading(true)
            const response = await getMyPanel()
            setPanel(response.data)
        } catch (error) {
            console.error('Error loading personal panel:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleRemoveItem = async (itemId) => {
        if (!window.confirm('Удалить элемент из панели?')) return
        
        try {
            await removeItemFromPanel(panel.id, itemId)
            // Обновляем локальное состояние
            setPanel(prev => ({
                ...prev,
                items: prev.items.filter(item => item.id !== itemId)
            }))
        } catch (error) {
            console.error('Error removing item:', error)
            alert('Ошибка при удалении элемента')
        }
    }

    const handleDragStart = (result) => {
        setDraggedItemId(result.draggableId)
        setDragOverIndex(result.source.index)
    }

    const handleDragUpdate = (result) => {
        if (!result.destination) {
            setDragOverIndex(result.source.index)
            return
        }
        setDragOverIndex(result.destination.index)
    }

    const handleDragEnd = async (result) => {
        setDraggedItemId(null)
        setDragOverIndex(null)

        if (!result.destination) return

        // Если элемент не переместился, не делаем запрос
        if (result.source.index === result.destination.index) {
            return
        }

        const items = Array.from(panel.items)
        const [reorderedItem] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, reorderedItem)

        // Обновляем order для каждого элемента
        const itemsOrder = items.map((item, index) => ({
            id: item.id,
            order: index
        }))

        // Обновляем локально сразу для лучшего UX
        setPanel(prev => ({ ...prev, items }))

        try {
            await reorderPanelItems(panel.id, itemsOrder)
        } catch (error) {
            console.error('Error reordering items:', error)
            // Восстанавливаем предыдущий порядок
            loadPanel()
        }
    }

    const handleItemClick = async (item) => {
        switch (item.item_type) {
            case 'anime':
                navigate(`/services/anime/watch/${item.item_id}`, {
                    state: {
                        anime: item.item_data
                    }
                })
                break
            case 'movie':
                navigate(`/services/movie/watch/${item.item_id}`, {
                    state: {
                        movie: item.item_data
                    }
                })
                break
            case 'chat':
                // Перейти на страницу чата
                navigate(`/services/community/chat/${item.item_id}`)
                break
            case 'forum':
                // Перейти на страницу форума
                navigate(`/services/community/forum/${item.item_id}`)
                break
            case 'server':
                // Только показываем блок, не переходим
                break
            default:
                break
        }
    }

    const renderItem = (item, index, provided, snapshot) => {
        const { item_type, item_data } = item
        const isDragging = snapshot?.isDragging || false
        const isDraggingOver = draggedItemId !== null && draggedItemId !== String(item.id)

        return (
            <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                className={`${styles.panelItem} ${isDragging ? styles.isDragging : ''} ${
                    isDraggingOver && dragOverIndex === index ? styles.dropTarget : ''
                }`}
                onClick={() => handleItemClick(item)}
            >
                <div className={styles.itemHeader}>
                    <span className={styles.itemType}>{item.item_type_display}</span>
                    <button
                        className={styles.removeBtn}
                        onClick={(e) => {
                            e.stopPropagation()
                            handleRemoveItem(item.id)
                        }}
                        title="Убрать из панели"
                    >
                        ✕
                    </button>
                </div>

                {(item_type === 'anime' || item_type === 'movie') && (
                    <div className={styles.mediaItem}>
                        <div className={styles.mediaPoster}>
                            <img src={item_data.poster || item_data.image} alt={item_data.title} />
                        </div>
                        <div className={styles.mediaInfo}>
                            <h3>{item_data.title}</h3>
                            <p>{item_data.description?.substring(0, 100)}...</p>
                        </div>
                    </div>
                )}

                {item_type === 'chat' && (
                    <div className={styles.chatItem}>
                        <div className={styles.chatIcon}>💬</div>
                        <div className={styles.chatInfo}>
                            <h3>{item_data.title || item_data.name}</h3>
                            <p>{item_data.description}</p>
                        </div>
                    </div>
                )}

                {item_type === 'forum' && (
                    <div className={styles.forumItem}>
                        <div className={styles.forumIcon}>📋</div>
                        <div className={styles.forumInfo}>
                            <h3>{item_data.title || item_data.name}</h3>
                            <p>{item_data.description}</p>
                        </div>
                    </div>
                )}

                {item_type === 'server' && (
                    <div className={styles.serverItem}>
                        <div className={styles.serverContent}>
                            <div className={styles.serverHeader}>
                                <div className={styles.serverTitle}>{item_data.title || item_data.name}</div>
                                {item_data.status && (
                                    <div className={`${styles.serverStatusBadge} ${styles[`status-${item_data.status}`]}`}>
                                        {item_data.status === 'online' && '🟢'}
                                        {item_data.status === 'offline' && '🔴'}
                                        {item_data.status === 'maintenance' && '🟠'}
                                        {item_data.status === 'loading' && '🔵'}
                                        <span className={styles.statusText}>
                                            {item_data.status === 'online' ? 'Online' : 
                                             item_data.status === 'offline' ? 'Offline' :
                                             item_data.status === 'maintenance' ? 'Maintenance' :
                                             'Loading'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {(item_data.ip_address || item_data.ip || item_data.host) && item_data.port && (
                                <button
                                    className={`${styles.addressButton} ${copiedId === `server-${item.id}` ? styles.copied : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        const host = item_data.ip_address || item_data.ip || item_data.host
                                        handleCopyWithNotification(`${host}:${item_data.port}`, `server-${item.id}`)
                                    }}
                                    title="Скопировать адрес"
                                >
                                    <span className={styles.addressText}>
                                        {(item_data.ip_address || item_data.ip || item_data.host)}:{item_data.port}
                                    </span>
                                    <span className={styles.addressIcon}>
                                        {copiedId === `server-${item.id}` ? '✓' : '📋'}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {item_type === 'music' && (
                    <div className={styles.musicItem}>
                        <AudioPlayer
                            src={item_data.file_url || item_data.url || item_data.audio_file || item_data.audio_url}
                            title={item_data.title}
                            artist={item_data.artist || item_data.album}
                            duration={item_data.duration}
                            shouldPause={playingMusicId !== null && playingMusicId !== item.id}
                            onPlayStatusChange={(isPlaying) => {
                                if (isPlaying) {
                                    setPlayingMusicId(item.id)
                                } else {
                                    setPlayingMusicId((prevId) => (prevId === item.id ? null : prevId))
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        )
    }

    if (loading) {
        return (
            <div className={styles.personalPanelPage}>
                <div className={styles.loading}>Загрузка личной панели...</div>
            </div>
        )
    }

    if (!isAuthenticated) {
        return (
            <div className={styles.personalPanelPage}>
                <div className={styles.authRequired}>
                    <div className={styles.authIcon}>🔒</div>
                    <h2>Требуется авторизация</h2>
                    <p>
                        Чтобы создать личную панель и закреплять здесь чаты, любимые аниме,
                        фильмы и сервера, вам нужно сначала авторизоваться.
                    </p>
                    <button
                        className={styles.loginBtn}
                        onClick={() => navigate('/')}
                    >
                        Авторизоваться
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.personalPanelPage}>
            <div className={styles.workspaceLayout}>
                <aside className={styles.notesSidebar}>
                    <div className={styles.notesHeader}>
                        <h2>Заметки</h2>
                        <p>Мгновенное автосохранение</p>
                    </div>

                    <div className={styles.notesTabs}>
                        <button
                            type="button"
                            className={`${styles.notesTabBtn} ${notesTab === 'notes' ? styles.notesTabBtnActive : ''}`}
                            onClick={() => setNotesTab('notes')}
                        >
                            Заметки
                        </button>
                        <button
                            type="button"
                            className={`${styles.notesTabBtn} ${notesTab === 'archive' ? styles.notesTabBtnActive : ''}`}
                            onClick={() => setNotesTab('archive')}
                        >
                            Архив
                        </button>
                    </div>

                    {notesTab === 'notes' && (
                        <div className={styles.newNoteBox}>
                            <textarea
                                ref={newNoteInputRef}
                                className={styles.newNoteInput}
                                placeholder="Новая заметка..."
                                value={newNoteText}
                                onInput={autoResizeTextarea}
                                onChange={(e) => {
                                    autoResizeTextarea(e)
                                    setNewNoteText(e.target.value)
                                }}
                                onBlur={handleCreateNoteOnBlur}
                            />
                        </div>
                    )}

                    <div className={styles.notesList}>
                        {notesLoading && (
                            <div className={styles.notesEmptyState}>Загрузка заметок...</div>
                        )}

                        {visibleNotes.length === 0 && (
                            <div className={styles.notesEmptyState}>
                                {notesTab === 'archive'
                                    ? 'Архив пуст'
                                    : 'Напишите первую заметку в поле выше'}
                            </div>
                        )}

                        {visibleNotes.map((note) => (
                            <div
                                key={note.id}
                                className={`${styles.noteCard} ${note.archived ? styles.noteCardArchived : ''}`}
                            >
                                <textarea
                                    ref={resizeTextareaElement}
                                    className={styles.noteInput}
                                    value={note.text}
                                    onInput={autoResizeTextarea}
                                    onChange={(e) => {
                                        autoResizeTextarea(e)
                                        handleUpdateNoteText(note.id, e.target.value)
                                    }}
                                    onBlur={() => handleNoteBlur(note.id, note.text)}
                                />

                                <div className={styles.noteFooter}>
                                    <span className={styles.noteDate}>
                                        {formatNoteDate(note.updated_at || note.created_at)}
                                    </span>

                                    <div className={styles.noteActionButtons}>
                                        {notesTab === 'notes' ? (
                                            <button
                                                type="button"
                                                className={styles.noteActionBtn}
                                                onClick={() => handleArchiveToggle(note.id, true)}
                                                title="Переместить в архив"
                                            >
                                                В архив
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className={styles.noteActionBtn}
                                                onClick={() => handleArchiveToggle(note.id, false)}
                                                title="Восстановить из архива"
                                            >
                                                Вернуть
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className={`${styles.noteActionBtn} ${styles.noteDeleteBtn}`}
                                            onClick={() => handleDeleteNote(note.id)}
                                            title="Удалить заметку"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </aside>

                <section className={styles.panelSection}>
                    {/* <div className={styles.hubNotice}>
                        <strong>Мой хаб</strong>
                        <span>Перетаскивайте карточки, чтобы быстро собрать удобный порядок</span>
                    </div> */}

                    {!panel || panel.items.length === 0 ? (
                        <div className={styles.emptyPanelInside}>
                            <div className={styles.emptyIcon}>📌</div>
                            <h2>Хаб пока пуст</h2>
                            <p>
                                Добавляйте карточки прямо из каталогов. Уже добавленные помечаются автоматически.
                            </p>
                            <ul className={styles.featureList}>
                                <li>🎬 Любимые аниме и фильмы</li>
                                <li>💬 Чаты и форумы</li>
                                <li>🖥️ Серверы</li>
                            </ul>
                        </div>
                    ) : (
                        <DragDropContext 
                            onDragStart={handleDragStart}
                            onDragUpdate={handleDragUpdate}
                            onDragEnd={handleDragEnd}
                        >
                            <Droppable droppableId="panel-items" type="PANEL_ITEM">
                                {(provided, snapshot) => (
                                    <div
                                        className={styles.panelGrid}
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                    >
                                        {panel.items.map((item, index) => (
                                            <Draggable
                                                key={item.id}
                                                draggableId={String(item.id)}
                                                index={index}
                                            >
                                                {(provided, snapshot) => renderItem(item, index, provided, snapshot)}
                                            </Draggable>
                                        ))}
                                        <div className={styles.placeholderSpace}>
                                            {provided.placeholder}
                                        </div>
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                </section>
            </div>

        </div>
    )
}

export default PersonalPanelPage
